// lib/services/compute/yuv_manager.ts

import { YuvFormat, YuvPlaneInfo, YuvUploadDescriptor } from "./yuvdefs";
import { PlaneLayoutLike } from "./yuv_format";

// 4:2:0 chroma dimension = ceil(n / 2), correct for odd coded sizes too.
function chroma_dim(n: number): number {
  return Math.floor((n + 1) / 2);
}

/**
 * Stateless utility for parsing VideoFrame plane layouts and reslicing
 * YUV byte buffers for GPU upload.
 *
 * In the previous engine YuvManager also owned GPU textures and uploaded
 * planes itself. That responsibility now lives in ColorCameraSlot (texture
 * ownership) and CaptureService (writeTexture calls). YuvManager is just
 * the byte-layout helper.
 *
 * Lifetime: one instance per CaptureService is fine. It keeps a single
 * reusable staging buffer for VideoFrame.copyTo, growing it on demand.
 */
export default class YuvManager {
  private staging_buffer: Uint8Array;

  constructor() {
    this.staging_buffer = new Uint8Array(1);
  }

  /**
   * Returns a staging buffer at least `size` bytes long. The same buffer
   * is reused frame-to-frame until a size increase forces reallocation.
   *
   * Callers pass this buffer to VideoFrame.copyTo and then to
   * build_descriptor + slice_plane.
   */
  ensure_staging_buffer(size: number): Uint8Array {
    if (!this.staging_buffer || this.staging_buffer.byteLength !== size) {
      this.staging_buffer = new Uint8Array(size);
    }
    return this.staging_buffer;
  }

  /**
   * Returns true if a buffer of the requested size required reallocation.
   * Useful for callers that need to know when a downstream resource (e.g.
   * GPU textures sized from frame dimensions) must be rebuilt.
   */
  resize_staging_buffer(size: number): boolean {
    if (!this.staging_buffer || this.staging_buffer.byteLength !== size) {
      this.staging_buffer = new Uint8Array(size);
      return true;
    }
    return false;
  }

  /**
   * Copy a span of bytes into a fresh GPU-writable Uint8Array. WebGPU's
   * writeTexture requires the source byte view to fully back its
   * underlying ArrayBuffer (no offset, full length), so we copy out of
   * the staging buffer rather than aliasing it.
   */
  slice_plane(
    data: Uint8Array,
    offset: number,
    length: number
  ): Uint8Array<ArrayBuffer> {
    const out = new Uint8Array(length);
    out.set(data.subarray(offset, offset + length));
    return out;
  }

  /**
   * Parse a copyTo PlaneLayout[] into a YuvUploadDescriptor for the given
   * already-resolved YUV format.
   *
   * Plane geometry is kept explicit and separated:
   *   - `width` / `height` are the LOGICAL texel dimensions of the plane's
   *     GPU texture (Y = coded, chroma = ceil(coded/2) for 4:2:0). They are
   *     NOT the row stride — row padding must never leak into the sampled
   *     texture width.
   *   - `stride` is the byte row pitch reported by copyTo, used only as
   *     writeTexture's bytesPerRow.
   *
   * Validates, and throws a descriptive Error on any inconsistency:
   *   - plane count strictly matches the format (I420=3, NV12=2),
   *   - each offset is a non-negative integer,
   *   - each stride covers at least one logical row of bytes,
   *   - each plane's bytes fit within the buffer:
   *       offset + stride*(height-1) + row_bytes <= data_byte_length.
   */
  build_descriptor(
    format: YuvFormat,
    layout: ReadonlyArray<PlaneLayoutLike>,
    data_byte_length: number,
    coded_width: number,
    coded_height: number,
    display_width: number,
    display_height: number
  ): YuvUploadDescriptor {
    if (!layout.length) {
      throw new Error("YuvManager.build_descriptor: layout is empty");
    }
    if (!(coded_width > 0) || !(coded_height > 0)) {
      throw new Error(
        `YuvManager.build_descriptor: invalid coded dimensions ${coded_width}x${coded_height}`
      );
    }

    const expected_planes = format === "I420" ? 3 : 2;
    if (layout.length !== expected_planes) {
      throw new Error(
        `YuvManager.build_descriptor: ${format} expected ${expected_planes} planes, got ${layout.length}`
      );
    }

    // Build one plane, validating offset/stride/bounds against the buffer.
    // `bytes_per_texel` is 1 for r8 (Y, I420 U/V) and 2 for rg8 (NV12 CbCr).
    const make_plane = (
      index: number,
      width: number,
      height: number,
      bytes_per_texel: number,
      label: string
    ): YuvPlaneInfo => {
      const offset = layout[index].offset;
      const stride = layout[index].stride;

      if (!Number.isFinite(offset) || offset < 0 || !Number.isInteger(offset)) {
        throw new Error(`${label}: invalid offset ${offset}`);
      }
      if (!Number.isFinite(stride) || stride <= 0) {
        throw new Error(`${label}: invalid stride ${stride}`);
      }

      const row_bytes = width * bytes_per_texel;
      if (stride < row_bytes) {
        throw new Error(
          `${label}: stride ${stride} is smaller than one logical row (${row_bytes} bytes for width ${width})`
        );
      }

      // Exact bytes writeTexture will read: full stride for every row except
      // the last, which needs only its logical row_bytes.
      const required = stride * (height - 1) + row_bytes;
      if (offset + required > data_byte_length) {
        throw new Error(
          `${label}: plane bytes out of bounds (offset ${offset} + required ${required} > buffer ${data_byte_length})`
        );
      }

      return { offset, stride, width, height, byteLength: required };
    };

    const cw = chroma_dim(coded_width);
    const ch = chroma_dim(coded_height);

    if (format === "I420") {
      return {
        format: "I420",
        codedWidth: coded_width,
        codedHeight: coded_height,
        displayWidth: display_width,
        displayHeight: display_height,
        planes: [
          make_plane(0, coded_width, coded_height, 1, "I420 Y"),
          make_plane(1, cw, ch, 1, "I420 U"),
          make_plane(2, cw, ch, 1, "I420 V"),
        ],
      };
    }

    // NV12: Y (r8) + interleaved CbCr (rg8, ceil(w/2) x ceil(h/2) texels).
    return {
      format: "NV12",
      codedWidth: coded_width,
      codedHeight: coded_height,
      displayWidth: display_width,
      displayHeight: display_height,
      planes: [
        make_plane(0, coded_width, coded_height, 1, "NV12 Y"),
        make_plane(1, cw, ch, 2, "NV12 CbCr"),
      ],
    };
  }
}